import { ModalBody, ModalContent, ModalHeader } from "@heroui/react";
import { formatUnits } from "viem";
import { formatTimestamp } from "../../utils";
import { TxHashLink } from "../TxHashLink";
import { TransactionTypeChip } from "../bridge/TransactionTypeChip";
import { ShieldingCard } from "../shielding/ShieldingCard";
import type { ShieldingTxFailed, ShieldingTxResult, ShieldingTxSuccess } from "../shielding/shieldingConfig";

export function ShieldingTxDetails({
  txData,
}: {
  txData: ShieldingTxResult;
}) {
  if (txData.txStatus === "success") {
    return <ShieldingTxSuccessDetails txData={txData} />;
  }
  return <ShieldingTxFailedDetails txData={txData} />;
}

function ShieldingTxSuccessDetails({
  txData,
}: {
  txData: ShieldingTxSuccess;
}) {
  const displayAmount = formatUnits(txData.amount, txData.tokenDecimals);

  return (
    <ModalContent>
      <ModalHeader>{txData.shieldingMode === "shield" ? "Shielding" : "Unshielding"} details</ModalHeader>
      <ModalBody>
        <ShieldingCard
          shieldingMode={txData.shieldingMode}
          displayAmount={Number(displayAmount)}
          tokenSymbol={txData.tokenSymbol}
        />
        <div className="w-full space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Transaction type</div>
            <TransactionTypeChip type="public" />
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Date & Time</div>
            <div className="font-semibold">{formatTimestamp(txData.timestamp)}</div>
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Transaction hash</div>
            <div className="font-semibold">
              <TxHashLink txHash={txData.txHash} source="l2" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Account address</div>
            <div className="font-semibold">
              <TxHashLink txHash={txData.l2AccountAddress.toString()} source="l2" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Network Fee</div>
            <div className="font-semibold">0.00005 ETH</div>
          </div>
        </div>
      </ModalBody>
    </ModalContent>
  );
}

function ShieldingTxFailedDetails({
  txData,
}: {
  txData: ShieldingTxFailed;
}) {
  return (
    <ModalContent>
      <ModalHeader>Shielding details</ModalHeader>
      <ModalBody>
        <ShieldingCard
          shieldingMode={txData.shieldingParams.shieldingMode}
          displayAmount={txData.shieldingParams.displayAmount}
          tokenSymbol={txData.shieldingParams.tokenSymbol}
        />

        <div className="w-full space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Status</div>
            <div className="font-semibold text-[#FF4D4D]">Failed</div>
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Transaction type</div>
            <TransactionTypeChip type="public" />
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-[#ADC0D0]">Date & Time</div>
            <div className="font-semibold">{formatTimestamp(txData.timestamp)}</div>
          </div>
        </div>

        <details className="w-full bg-[#3F6784] p-4 rounded-2xl space-y-3 text-sm">
          <summary className="font-semibold text-[#ADC0D0] cursor-pointer">Error message</summary>
          <div className="font-semibold text-[#FF4D4D] mt-2 overflow-auto max-h-48">{txData.errorMessage}</div>
        </details>
      </ModalBody>
    </ModalContent>
  );
}
